import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { IIASACEServer } from '../../interfaces/iasace.interface';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';

const CACHE_TIME: number = 3600;

export class IASACEServerPlugin extends SmartenitPlugin
  implements IIASACEServer {
  public onInit() {
    this.getCachedValues();
  }

  /*
    *AlarmStatus*
    0x00 No alarm
    0x01 Burglar
    0x02 Fire
    0x03 Emergency
    0x04 Police Panic
    0x05 Fire Panic
    0x06 Emergency Panic (i.e., medical issue)

    *AudibleNotification*
    0x00 Mute (i.e., no audible notification)
    0x01 Default sound
    0x80-0xff Manufacturer specific
  */
  private _armResponse: any;
  private _alarmStatus: any;
  private _bypassedZoneList: any = [];
  private _zones: any = [];

  public setEmergency(type: string) {
    let method: string | null = null;
    switch (type) {
      case 'emergency':
        method = 'Emergency';
        break;
      case 'fire':
        method = 'Fire';
        break;
      case 'panic':
        method = 'Panic';
        break;
    }

    if (method) {
      this.device.executeMethod(
        this.componentId,
        this.processorName,
        method,
        {},
        true
      );
    }
  }

  /*
    *ArmMode*
    0x00 Disarm
    0x01 Arm Day/Home Zones Only // ARM stay
    0x02 Arm Night/Sleep Zones Only // ARM stay
    0x03 Arm All Zones // ARM away
  */
  public arm(mode: number): void {
    this.executeMethod(
      this.componentId,
      this.processorName,
      'Arm',
      { ArmMode: mode, ArmDisarmCode: '1234', ZoneID: 1 },
      true,
      null,
      'cached=false'
    );
  }

  public disarm(): void {
    this.executeMethod(
      this.componentId,
      this.processorName,
      'Arm',
      { ArmMode: 0, ArmDisarmCode: '1234', ZoneID: 1 },
      true,
      null,
      'cached=false'
    );
  }

  public getZoneInformation(zoneId: number): void {
    this.executeMethod(
      this.componentId,
      this.processorName,
      'GetZoneInformation',
      { ZoneID: zoneId },
      true
    );
  }

  /**
   * Bypass command
   * @param ZoneIDArray Contains the id of the index of each Zone
   *  in the CIE's zone table eg. [1,3,3,4]
   * @param ArmDisarmCode Should be between four and eight
   *  alphanumeric characters in length.
   */
  public bypass(ZoneIDArray = [], ArmDisarmCode: string = ''): void {
    const method = 'Bypass';
    const payload = { ZoneIDArray, ArmDisarmCode };
    const requestStatus = true;
    this.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      requestStatus
    );
  }

  public bypassZone(zoneId: number, armDisarmCode: string = ''): void {
    let uniqueArray = [];
    const zoneIdx = this._bypassedZoneList.indexOf(zoneId);

    if (zoneIdx > -1) {
      uniqueArray = this._bypassedZoneList.filter(
        (zone: any, idx: number) => idx !== zoneIdx
      );
    } else {
      uniqueArray = [zoneId].concat(this._bypassedZoneList);
    }

    this.bypass(uniqueArray, armDisarmCode);
  }

  public getZones(): any {
    if (this.device && this.device.meta && this.device.meta.zones) {
      return this.device.meta.zones;
    }

    return [];
  }

  public getBypassedZoneList(): void {
    const method = 'GetBypassedZoneList';
    const payload = {};
    const requestStatus = true;
    this.executeMethod(
      this.componentId,
      this.processorName,
      method,
      payload,
      requestStatus
    );
  }

  public getEffectOptions(): any {
    return [];
  }

  public getEffectAttribute(): string | null {
    return null;
  }

  public getEffectMethod(context?: any): string | null {
    return null;
  }

  public getConditionOptions(): any {
    return [
      {
        displayName: _.capitalize(_.get(
          this.translationService.getResource(),
          'DEVICES.IAS.LABEL.DISARMED',
          ''
        )),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Panel disarmed',
        value: { '#eq': 0x00 },
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.ARMED_STAY',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Armed stay',
        value: { '#eq': 0x01 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.ARMED_NIGHT',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Armed night',
        value: { '#eq': 0x02 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.ARMED_AWAY',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Armed away',
        value: { '#eq': 0x03 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.EXIT_DELAY',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Exit delay',
        value: { '#eq': 0x04 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.ENTRY_DELAY',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Entry delay',
        value: { '#eq': 0x05 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.NOT_READY_TO_ARM',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Not Ready To Arm',
        value: { '#eq': 0x06 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.IN_ALARM',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'In Alarm',
        value: { '#eq': 0x07 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.ARMING_STAY',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Arming Stay',
        value: { '#eq': 0x08 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.ARMING_NIGHT',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Arming Night',
        value: { '#eq': 0x09 }
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.IAS.LABEL.ARMING_AWAY',
            ''
          )
        ),
        method: {
          name: 'PanelStatusChanged'
        },
        name: 'Arming Away',
        value: { '#eq': 0x0a }
      }
    ];
  }

  public getConditionAttribute(): string | null {
    return null;
  }

  public getCachedValues(subscribe?: boolean): any {
    const cacheValues = [
      this.getCache('ArmResponse'),
      this.getCache('AlarmStatus'),
      this.getCache('ZoneIDArray'),
      this.getCache()
    ];

    const observable = Observable.forkJoin(cacheValues).flatMap((array) => {
      if (array && array[0] != null) {
        this._armResponse = array[0];
      }

      if (array && array[1] != null) {
        this._alarmStatus = array[1];
      }

      if (array && array[2] != null) {
        this._bypassedZoneList = array[2];
      }

      this._zones =
        this.device && this.device.meta && this.device.meta.zones
          ? this.device.meta.zones
          : [];

      if (array && array[3] != null) {
        this.state = array[3];
      }

      if (
        this.state != null &&
        (this.state.constructor !== Object ||
          Object.keys(this.state).length > 0)
      ) {
        this.device.cached = true;
      }

      this._onUpdate.next({
        alarmStatus: this._alarmStatus,
        armResponse: this._armResponse,
        bypassedZoneList: this._bypassedZoneList,
        state: this.state,
        zones: this._zones
      });

      return Observable.of(this.state);
    });

    if (subscribe === true) {
      return observable;
    }

    const subscription: Subscription = observable.subscribe(() =>
      subscription.unsubscribe()
    );
  }

  /*
    *PanelStatus*
    0x00 Panel disarmed (all zones disarmed) and ready to arm
    0x01 Armed stay
    0x02 Armed night
    0x03 Armed away
    0x04 Exit delay
    0x05 Entry delay
    0x06 Not ready to arm
    0x07 In alarm
    0x08 Arming Stay
    0x09 Arming Night
    0x0a Arming Away
  */

  /*
    *ArmNotification*:
    0x00 All Zones Disarmed
    0x01 Only Day/Home Zones Armed
    0x02 Only Night/Sleep Zones Armed
    0x03 All Zones Armed
    0x04 Invalid Arm/Disarm Code
    0x05 Not ready to arm*
    0x06 Already disarmed
  */

  // { "AlarmStatus" : "0x01", "AudibleNotification" : "0x01", "PanelStatus" : "0x03", "SecondsRemaining" : 0 }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attributeOrMethod = message && message.attributeOrMethod;
    const response = message && message.data && message.data.response;

    if (
      attributeOrMethod === 'GetPanelStatusResponse' ||
      attributeOrMethod === 'PanelStatusChanged'
    ) {
      this.state = parseInt(response.value.PanelStatus);
      this._alarmStatus = parseInt(response.value.AlarmStatus);
      this.setCache(
        'AlarmStatus',
        this._alarmStatus,
        CACHE_TIME,
        undefined,
        timestamp
      );
      this._onUpdate.next({
        alarmStatus: this._alarmStatus,
        state: this.state
      });
    } else if (attributeOrMethod == 'ArmResponse') {
      this._armResponse = response.value.ArmNotification;
      this.setCache(
        'ArmResponse',
        this._armResponse,
        CACHE_TIME,
        undefined,
        timestamp
      );
      this._onUpdate.next({ armResponse: this._armResponse });
    } else if (attributeOrMethod === 'SetBypassedZoneList') {
      this._bypassedZoneList = response.value.ZoneIDArray;
      this.updateZonesBypassed(this._bypassedZoneList);
      this.setCache(
        'ZoneIDArray',
        this._bypassedZoneList,
        CACHE_TIME,
        undefined,
        timestamp
      );
      this._onUpdate.next({ bypassedZoneList: this._bypassedZoneList });
    } else if (attributeOrMethod === 'BypassResponse') {
      // this.executeMethod(this.componentId, this.processorName, 'GetBypassedZoneList', {});
    } else if (attributeOrMethod === 'ZoneMap') {
      this.devicesService.list().subscribe((devices: any) => {
        this.parseZoneMap(response.value, (devices && devices.data) || []);
      });
    }

    return this.state;
  }

  public getStatusPayload(): any {
    return [
      'GetPanelStatusResponse',
      'SetBypassedZoneList',
      'ZoneMap',
      'PanelStatusChanged'
    ];
  }

  public getStatus(
    context?: string,
    force?: boolean,
    asObservable?: boolean,
    payload?: any,
    sendDirect?: boolean
  ) {
    super.getStatus(context, true, asObservable, payload, sendDirect);
  }

  public arraysOfNumbersAreEqual(array1 = [], array2 = []) {
    if (array1.length !== array2.length) {
      return false;
    }

    array1.sort();
    array2.sort();

    let i = 0;
    for (; i < array2.length; i += 1) {
      if (parseInt(array1[i]) !== parseInt(array2[i])) {
        return false;
      }
    }

    return true;
  }

  public parseZoneMap(response: any, devices: any) {
    let zonesMap: any = [];
    Object.keys(response)
      .sort()
      .forEach((key) => {
        zonesMap.push(response[key]);
      });

    zonesMap = zonesMap.filter((zone: any) => {
      return (
        zone.hasOwnProperty('ZoneID') &&
        zone.hasOwnProperty('IEEEaddress') &&
        zone.hasOwnProperty('ZoneType')
      );
    });

    let requestDevices: boolean = false;
    zonesMap = zonesMap.map((zone: any) => {
      const zoneMap: any = {
        bypassed: this._bypassedZoneList.indexOf(zone.ZoneID) > -1,
        hwId: zone.IEEEaddress.replace(/0x/g, '').toLowerCase(),
        zoneId: zone.ZoneID,
        zoneType: zone.ZoneType.toLowerCase()
      };

      const currentZone: any = this._zones.find((oldZone: any) => {
        return (
          oldZone.deviceId &&
          devices.some((d: any) => d._id === oldZone.deviceId) &&
          oldZone.zoneId == zoneMap.zoneId &&
          oldZone.hwId == zoneMap.hwId
        );
      });

      requestDevices = requestDevices || (currentZone ? false : true);
      zoneMap.name = currentZone ? currentZone.name : 'unknown';
      zoneMap.deviceId = currentZone ? currentZone.deviceId : 'unknown';

      return zoneMap;
    });

    if (this._zones.length != zonesMap.length && zonesMap.length > 0) {
      requestDevices = true;
    }

    this._zones = zonesMap;

    const unkownDeviceIds = this._zones.map((zone: any) => zone.hwId);

    if (!requestDevices || unkownDeviceIds.length === 0) {
      return;
    }

    // Get deviceId by HardwareId
    // TODO (jjescof): Pagination issue
    const query = { hwId: { $in: unkownDeviceIds } };
    this.devicesService
      .list(query)
      .flatMap((res) => Observable.of(res.data))
      .flatMap((devicesRes) => {
        return Observable.of(devicesRes).map((devs) => {
          return devs.map((device: any) => {
            return {
              deviceId: device._id,
              hwId: device.hwId,
              name: device.name
            };
          });
        });
      })
      .flatMap((devs) => {
        devs.forEach((device: any) => {
          const zoneIndex: number = this._zones.findIndex((zone: any) => {
            return zone.hwId == device.hwId;
          });

          if (zoneIndex > -1) {
            const zone = this._zones[zoneIndex];
            const newZone = Object.assign({}, zone, {
              deviceId: device.deviceId,
              name: device.name
            });
            this._zones[zoneIndex] = newZone;
          }
        });

        return this.updateZonesMeta();
      })
      .subscribe(() => {
        // TODO (jjescof) zoneStatus is reading from the iaszone directly, should it be parsed here?
        this._onUpdate.next({
          zones: this._zones
        });
      });
  }

  private updateZonesBypassed(bypassList: any) {
    this._zones.forEach((zone: any) => {
      zone.bypassed = bypassList.indexOf(zone.zoneId) > -1;
    });

    this.updateZonesMeta();

    this._onUpdate.next({
      zones: this._zones
    });
  }

  private updateZonesMeta(): Observable<any> {
    this.device.meta = this.device.meta || {};
    if (!_.isEqual(this.device.meta.zones, this._zones)) {
      this.device.meta.zones = this._zones;
      this.device.data.overwriteMeta = true;
      return this.device.save();
    }
    return Observable.of(true);
  }
}
